import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  VpnKey as VpnKeyIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import axios from "axios";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

const UserManagement = () => {
  const { hasPermission } = usePermissions();
  const canManageUsers = hasPermission(CAPABILITIES.adminUsers, "X");
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null });
  const [mfaResetConfirm, setMfaResetConfirm] = useState({ open: false, user: null });
  const [mfaResetSubmitting, setMfaResetSubmitting] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    userName: "",
    password: "",
    entityId: 0,
    active: 1,
    useLastSelectedEntity: 1,
    lastSelectedEntityId: 0,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/v1/AppUsers");
      const data = response.data;
      setAllUsers(data);
      sortUsers(data, sortColumn, sortDirection);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sortUsers = (data, column, direction) => {
    const sorted = [...data].sort((a, b) => {
      let valueA, valueB;

      switch (column) {
        case "name":
          valueA = `${a.firstname || ""} ${a.lastname || ""}`.trim().toLowerCase();
          valueB = `${b.firstname || ""} ${b.lastname || ""}`.trim().toLowerCase();
          break;
        case "email":
          valueA = (a.email || "").toLowerCase();
          valueB = (b.email || "").toLowerCase();
          break;
        case "username":
          valueA = (a.userName || "").toLowerCase();
          valueB = (b.userName || "").toLowerCase();
          break;
        case "status":
          valueA = a.active === 1 ? "active" : "inactive";
          valueB = b.active === 1 ? "active" : "inactive";
          break;
        case "mfa":
          valueA = a.twoFactorEnabled ? 1 : 0;
          valueB = b.twoFactorEnabled ? 1 : 0;
          break;
        case "created":
          valueA = a.creationDate ? new Date(a.creationDate).getTime() : 0;
          valueB = b.creationDate ? new Date(b.creationDate).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return direction === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else {
        return direction === "asc" ? valueA - valueB : valueB - valueA;
      }
    });

    setUsers(sorted);
  };

  const handleSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
    sortUsers(allUsers, column, newDirection);
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        firstName: user.firstname || "",
        lastName: user.lastname || "",
        email: user.email || "",
        userName: user.userName || "",
        password: "",
        entityId: 0,
        active: typeof user.active === "number" ? user.active : 1,
        useLastSelectedEntity: user.useLastSelectedEntity || 1,
        lastSelectedEntityId: user.lastSelectedEntityId || 0,
      });
    } else {
      setSelectedUser(null);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        userName: "",
        password: "",
        entityId: 0,
        active: 1,
        useLastSelectedEntity: 1,
        lastSelectedEntityId: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      userName: "",
      password: "",
      entityId: 0,
      active: 1,
      useLastSelectedEntity: 1,
      lastSelectedEntityId: 0,
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      let url = selectedUser ? `/api/v1/UpdateAppUser/${selectedUser.id}` : "/api/v1/CreateAppUser";
      let method = selectedUser ? "put" : "post";
      let payload;
      if (selectedUser) {
        payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          entityId: 0,
          active: formData.active,
          password: formData.password,
        };
      } else {
        payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          userName: formData.email,
          entityId: 0,
          active: formData.active,
          password: formData.password,
        };
      }

      if (selectedUser) {
        await axios.put(url, payload);
      } else {
        await axios.post(url, payload);
      }

      await fetchUsers();
      handleCloseDialog();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`/api/v1/DeleteAppUser/${userId}`);
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteClick = (userId) => {
    setDeleteConfirm({ open: true, userId });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.userId) {
      await handleDelete(deleteConfirm.userId);
    }
    setDeleteConfirm({ open: false, userId: null });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, userId: null });
  };

  const handleMfaResetClick = (user) => {
    setSuccess("");
    setError("");
    setMfaResetConfirm({ open: true, user });
  };

  const handleMfaResetCancel = () => {
    if (mfaResetSubmitting) {
      return;
    }
    setMfaResetConfirm({ open: false, user: null });
  };

  const handleMfaResetConfirm = async () => {
    const user = mfaResetConfirm.user;
    if (!user?.id) {
      return;
    }

    try {
      setMfaResetSubmitting(true);
      setError("");
      const response = await axios.post(`/api/v1/AppUsers/${user.id}/disable-mfa`);
      setSuccess(response.data?.message || "MFA has been disabled for this user.");
      setMfaResetConfirm({ open: false, user: null });
      await fetchUsers();
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data || err.message;
      setError(typeof message === "string" ? message : "MFA reset failed.");
    } finally {
      setMfaResetSubmitting(false);
    }
  };

  const handleOpenPasswordDialog = (user) => {
    setPasswordDialog({ open: true, user });
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setPasswordError("");
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialog({ open: false, user: null });
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setPasswordError("");
  };

  const handlePasswordSubmit = async () => {
    const user = passwordDialog.user;
    if (!user) {
      return;
    }

    if (!newPassword.trim()) {
      setPasswordError("Voer een nieuw wachtwoord in.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Wachtwoorden komen niet overeen.");
      return;
    }

    try {
      setPasswordSubmitting(true);
      setPasswordError("");

      await axios.put(`/api/v1/UpdateAppUser/${user.id}`, {
        firstName: user.firstname || "",
        lastName: user.lastname || "",
        email: user.email || "",
        entityId: user.entityId || 0,
        active: typeof user.active === "number" ? user.active : 1,
        password: newPassword,
      });

      handleClosePasswordDialog();
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data || err.message;
      setPasswordError(typeof message === "string" ? message : "Wachtwoord wijzigen mislukt.");
    } finally {
      setPasswordSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Management</Typography>
        {canManageUsers && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add User
        </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": {
                  py: 0.5,
                  px: 1.5,
                },
                "& .MuiTableRow-root": {
                  height: 32,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() => handleSort("name")}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Name
                      {sortColumn === "name" &&
                        (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("email")}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Email
                      {sortColumn === "email" &&
                        (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("username")}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Username
                      {sortColumn === "username" &&
                        (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("status")}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Status
                      {sortColumn === "status" &&
                        (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("mfa")}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      MFA
                      {sortColumn === "mfa" &&
                        (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("created")}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Created
                      {sortColumn === "created" &&
                        (sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                    </Box>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{`${user.firstname || ""} ${user.lastname || ""}`}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.userName}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.active === 1 ? "Active" : "Inactive"}
                        color={user.active === 1 ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.twoFactorEnabled ? "Enabled" : "Off"}
                        color={user.twoFactorEnabled ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.creationDate ? new Date(user.creationDate).toLocaleDateString() : ""}</TableCell>
                    <TableCell>
                      {canManageUsers && (
                      <>
                      <Tooltip title="Bewerken">
                        <IconButton size="small" onClick={() => handleOpenDialog(user)} color="primary" aria-label="Gebruiker bewerken">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Wachtwoord wijzigen">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenPasswordDialog(user)}
                          color="secondary"
                          aria-label="Wachtwoord wijzigen"
                        >
                          <VpnKeyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.twoFactorEnabled ? "MFA resetten" : "MFA is al uitgeschakeld"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleMfaResetClick(user)}
                            color="warning"
                            disabled={!user.twoFactorEnabled}
                            aria-label={`MFA resetten voor ${user.email || user.userName || "gebruiker"}`}
                          >
                            <LockResetIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Verwijderen">
                        <IconButton size="small" onClick={() => handleDeleteClick(user.id)} color="error" aria-label="Gebruiker verwijderen">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedUser ? "Edit User" : "Add New User"}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              fullWidth
              inputProps={{ maxLength: 40 }}
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              fullWidth
              inputProps={{ maxLength: 40 }}
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              fullWidth
            />
            {!selectedUser && (
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                fullWidth
                inputProps={{ maxLength: 60 }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            )}
            <FormControlLabel
              control={
                <Switch checked={formData.active === 1} onChange={(e) => handleInputChange("active", e.target.checked ? 1 : 0)} />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedUser ? "Save Changes" : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirm.open} onClose={handleDeleteCancel}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mfaResetConfirm.open} onClose={handleMfaResetCancel}>
        <DialogTitle>Reset MFA</DialogTitle>
        <DialogContent>
          <Typography>
            Disable MFA for {mfaResetConfirm.user?.email || "this user"}? They will need to set up MFA again, and
            existing refresh tokens will be invalidated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMfaResetCancel} disabled={mfaResetSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleMfaResetConfirm} color="warning" variant="contained" disabled={mfaResetSubmitting}>
            {mfaResetSubmitting ? "Resetting..." : "Reset MFA"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialog.open} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Wachtwoord wijzigen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {passwordDialog.user && (
              <Typography variant="body2" color="text.secondary">
                Wijzig het wachtwoord voor {passwordDialog.user.email}
              </Typography>
            )}

            {passwordError && (
              <Alert severity="error">{passwordError}</Alert>
            )}

            <TextField
              label="Nieuw wachtwoord"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 60 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      aria-label={showNewPassword ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Bevestig wachtwoord"
              type={showNewPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 60 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={passwordSubmitting}>
            Annuleren
          </Button>
          <Button onClick={handlePasswordSubmit} variant="contained" disabled={passwordSubmitting}>
            {passwordSubmitting ? "Opslaan..." : "Wachtwoord opslaan"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
